import { useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Calculator, TrendingUp, TrendingDown, Wallet, Plus, Pencil, Trash2, FileClock, AlertTriangle, Repeat, Info } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const PERIODS = [
  { k: "day", label: "Aujourd'hui" },
  { k: "week", label: "Cette semaine" },
  { k: "month", label: "Ce mois" },
  { k: "year", label: "Cette année" },
  { k: "custom", label: "Personnalisée" },
];

const HORS_CATS = ["Achat produits (France)", "Fret", "Matériel", "Eau", "Électricité", "Transport", "Carte bancaire", "Virement", "Avance perso", "Autre"];
const METHODS = ["Espèces", "Carte bancaire", "Virement", "Chèque", "Mobile money"];
const DEBT_TYPES = ["dette", "avance", "apport", "retrait", "remboursement"];

const today = () => new Date().toISOString().slice(0, 10);
const fmtD = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

export default function Accounting() {
  const { settings } = useAuth();
  const cur = settings?.currency || "KMF";
  const m = (n) => fmtMoney(n || 0, cur);

  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [trends, setTrends] = useState([]);
  const [upcoming, setUpcoming] = useState(null);
  const [detail, setDetail] = useState(null); // {title, key}

  const loadSummary = useCallback(async () => {
    const params = period === "custom" ? { period, date_from: from, date_to: to } : { period, date: today() };
    try { const { data } = await api.get("/acct/summary", { params }); setSummary(data); }
    catch (e) { toast.error(apiError(e)); }
  }, [period, from, to]);

  const loadEmployees = useCallback(async () => {
    try { const { data } = await api.get("/acct/employees"); setEmployees(data); } catch { /* ignore */ }
  }, []);

  const loadTrends = useCallback(async () => {
    try {
      const [tr, up] = await Promise.all([api.get("/acct/trends?months=12"), api.get("/acct/upcoming")]);
      setTrends(tr.data); setUpcoming(up.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { loadTrends(); }, [loadTrends]);
  const reloadAll = useCallback(() => { loadSummary(); loadTrends(); }, [loadSummary, loadTrends]);

  const s = summary;
  const resultat = s?.resultat?.resultat ?? 0;

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white grid place-items-center"><Calculator size={20} /></div>
        <div>
          <h1 className="text-2xl font-display font-extrabold text-slate-800">Comptabilité</h1>
          <p className="text-slate-500 text-sm">Pilotage financier — données de la caisse récupérées automatiquement</p>
        </div>
      </div>

      {/* Sélecteur de période */}
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <Button key={p.k} data-testid={`period-${p.k}`} size="sm" variant={period === p.k ? "default" : "outline"}
            className={period === p.k ? "bg-indigo-600 hover:bg-indigo-700" : ""} onClick={() => setPeriod(p.k)}>{p.label}</Button>
        ))}
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="custom-from" />
            <span className="text-slate-400">→</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="custom-to" />
          </div>
        )}
        {s && <span className="text-xs text-slate-400 ml-auto">{fmtD(s.period.start)} → {fmtD(s.period.end)}</span>}
      </div>

      {s && s.tresorerie.init_date && (
        <div className="flex items-center gap-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2" data-testid="init-banner">
          <Info size={14} /> Comptabilité active depuis le <strong>{fmtD(s.tresorerie.init_date)}</strong> — rien avant cette date n'est compté.
        </div>
      )}

      {/* Graphique tendances 12 mois */}
      {trends.length > 0 && (
        <Card className="p-4" data-testid="trends-chart">
          <div className="flex items-center gap-2 text-slate-700 font-semibold mb-3"><TrendingUp size={18} /> Évolution sur 12 mois</div>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={trends} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
              <Tooltip formatter={(v) => m(v)} />
              <Legend />
              <Bar dataKey="recettes" name="Recettes réelles" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="charges" name="Total charges" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="resultat" name="Résultat" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Échéances (charges à payer) */}
      {upcoming && (upcoming.overdue.length > 0 || upcoming.upcoming.length > 0) && (
        <Card className="p-4" data-testid="echeances-card">
          <div className="flex items-center gap-2 text-amber-600 font-semibold mb-3"><AlertTriangle size={18} /> Échéances à payer</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-red-600 mb-1">En retard · {m(upcoming.overdue_total)}</p>
              {upcoming.overdue.length === 0 && <p className="text-xs text-slate-400">Aucune</p>}
              {upcoming.overdue.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-50">
                  <span className="text-slate-600"><span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded mr-1">{i.kind}</span>{i.label} · {fmtD(i.due_date)}</span>
                  <span className="font-semibold">{m(i.amount)}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-medium text-sky-600 mb-1">À venir · {m(upcoming.upcoming_total)}</p>
              {upcoming.upcoming.length === 0 && <p className="text-xs text-slate-400">Aucune</p>}
              {upcoming.upcoming.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-50">
                  <span className="text-slate-600"><span className="text-xs bg-sky-50 text-sky-600 px-1.5 py-0.5 rounded mr-1">{i.kind}</span>{i.label} · {fmtD(i.due_date)}</span>
                  <span className="font-semibold">{m(i.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Cartes tableau de bord */}
      {s && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recettes */}
          <Card className="p-4 space-y-2" data-testid="card-recettes">
            <div className="flex items-center gap-2 text-emerald-600 font-semibold"><TrendingUp size={18} /> Recettes réelles</div>
            <Kpi label="Cumul réel comptabilisé" value={m(s.recettes.reelles)} big onClick={() => setDetail({ key: "closings", title: "Clôtures de la période" })} testid="kpi-recettes" tip="Somme des montants réels comptabilisés des clôtures de la période (espèces comptées + sorties de caisse + autres moyens). C'est l'argent réellement entré sur la période." />
            <Kpi label="Espèces réellement comptées" value={m(s.recettes.especes)} />
            <Kpi label="Autres moyens de paiement" value={m(s.recettes.autres_moyens)} />
          </Card>

          {/* Dépenses */}
          <Card className="p-4 space-y-2" data-testid="card-depenses">
            <div className="flex items-center gap-2 text-red-600 font-semibold"><TrendingDown size={18} /> Dépenses / Charges</div>
            <Kpi label="Sorties de caisse (agents)" value={m(s.depenses.sorties_caisse)} onClick={() => setDetail({ key: "sorties_caisse", title: "Sorties de caisse" })} testid="kpi-sorties" />
            <Kpi label="Dépenses hors caisse" value={m(s.depenses.hors_caisse)} onClick={() => setDetail({ key: "hors_caisse", title: "Dépenses hors caisse" })} testid="kpi-hors" />
            <Kpi label="Salaires versés" value={m(s.depenses.salaires)} onClick={() => setDetail({ key: "salaires", title: "Salaires versés" })} />
            <Kpi label="Loyer payé" value={m(s.depenses.loyer)} onClick={() => setDetail({ key: "loyer", title: "Loyer" })} />
            <div className="border-t pt-2"><Kpi label="Total des charges" value={m(s.depenses.total)} big /></div>
          </Card>

          {/* Résultat */}
          <Card className="p-4 space-y-2" data-testid="card-resultat">
            <div className="flex items-center gap-2 text-indigo-600 font-semibold"><Calculator size={18} /> Résultat</div>
            <Kpi label="Cumul réel après dépenses" value={m(s.resultat.reel_apres_depenses)} big testid="kpi-apres-depenses" tip="Recettes réelles − sorties de caisse (agents). Indique ce qu'il reste après les dépenses quotidiennes réglées en espèces dans la caisse." />
            <div className={`rounded-lg p-3 ${resultat >= 0 ? "bg-emerald-50" : "bg-red-50"}`}>
              <p className="text-xs text-slate-500">{resultat >= 0 ? "Bénéfice réel" : "Perte réelle"}<InfoTip text="Performance économique de la période = Recettes réelles − toutes les charges (sorties de caisse, dépenses hors caisse, salaires, loyer). C'est indépendant de la trésorerie : on peut être bénéficiaire sans avoir beaucoup de liquidités si des clients n'ont pas encore payé." /></p>
              <p className={`text-2xl font-extrabold ${resultat >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="kpi-resultat">{m(resultat)}</p>
            </div>
            <Kpi label="Charges restant à payer" value={m(s.resultat.charges_restantes)} />
          </Card>

          {/* Trésorerie + Créances */}
          <Card className="p-4 space-y-2" data-testid="card-tresorerie">
            <div className="flex items-center gap-2 text-sky-600 font-semibold"><Wallet size={18} /> Trésorerie</div>
            <Kpi label="Trésorerie disponible" value={m(s.tresorerie.disponible)} big testid="kpi-treso" tip="Argent réellement disponible aujourd'hui (caisse + banque) depuis la date d'initialisation : trésorerie de départ + recettes réelles (clôtures) + encaissements du jour non encore clôturés − toutes les sorties d'argent (sorties de caisse, dépenses hors caisse, salaires, loyer, remboursements/retraits) + apports." />
            <Kpi label="Trésorerie prévisionnelle" value={m(s.tresorerie.previsionnelle)} tip="Projection = trésorerie disponible + créances à encaisser (ce que les clients vous doivent) − charges restant à payer (salaires, loyer, dettes non soldés). C'est ce qu'il vous restera une fois tout réglé." />
            <div className="border-t pt-2">
              <Kpi label="Créances (reste à encaisser)" value={m(s.creances.reste)} onClick={() => setDetail({ key: "creances", title: "Restes à encaisser (créances)" })} testid="kpi-creances" />
              <p className="text-xs text-slate-400">Facturé {m(s.creances.total_facture)} · Encaissé {m(s.creances.total_encaisse)}</p>
            </div>
          </Card>
        </div>
      )}

      {/* Gestion */}
      <Tabs defaultValue="hors" className="w-full">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="hors" data-testid="tab-hors">Dépenses hors caisse</TabsTrigger>
          <TabsTrigger value="salaires" data-testid="tab-salaires">Salaires</TabsTrigger>
          <TabsTrigger value="employes" data-testid="tab-employes">Employés</TabsTrigger>
          <TabsTrigger value="loyer" data-testid="tab-loyer">Loyer</TabsTrigger>
          <TabsTrigger value="dettes" data-testid="tab-dettes">Dettes / Apports</TabsTrigger>
          <TabsTrigger value="recurrentes" data-testid="tab-recurrentes">Récurrentes</TabsTrigger>
          <TabsTrigger value="init" data-testid="tab-init">Initialisation</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">Journal</TabsTrigger>
        </TabsList>

        <TabsContent value="hors">
          <CrudSection title="Dépenses hors caisse" endpoint="/acct/expenses" onMutate={reloadAll}
            fields={[
              { key: "date", label: "Date", type: "date", default: today() },
              { key: "category", label: "Catégorie", type: "select", options: HORS_CATS, default: HORS_CATS[0] },
              { key: "amount", label: "Montant", type: "number", req: true },
              { key: "method", label: "Moyen", type: "select", options: METHODS, default: "Carte bancaire" },
              { key: "note", label: "Note", type: "text" },
            ]}
            columns={[{ key: "date", label: "Date", date: true }, { key: "category", label: "Catégorie" }, { key: "amount", label: "Montant", money: true }, { key: "method", label: "Moyen" }, { key: "note", label: "Note" }]} />
        </TabsContent>

        <TabsContent value="salaires">
          <CrudSection title="Salaires" endpoint="/acct/salaries" onMutate={reloadAll}
            fields={[
              { key: "employee_id", label: "Employé", type: "select", options: employees.map((e) => ({ value: e.id, label: e.name })), req: true },
              { key: "label", label: "Période (ex: Juillet 2026)", type: "text", default: "" },
              { key: "planned", label: "Salaire prévu", type: "number" },
              { key: "advance", label: "Avance", type: "number" },
              { key: "bonus", label: "Prime", type: "number" },
              { key: "paid", label: "Montant payé", type: "number" },
              { key: "pay_date", label: "Date de paiement", type: "date", default: today() },
              { key: "method", label: "Mode", type: "select", options: METHODS, default: "Espèces" },
              { key: "note", label: "Note", type: "text" },
            ]}
            columns={[{ key: "pay_date", label: "Date", date: true }, { key: "employee_name", label: "Employé" }, { key: "label", label: "Période" }, { key: "disbursed", label: "Décaissé", money: true }, { key: "remaining", label: "Reste", money: true }, { key: "status", label: "Statut", badge: true }]}
            hint="Si le mode est « Espèces », une sortie de caisse est créée automatiquement." />
        </TabsContent>

        <TabsContent value="employes">
          <CrudSection title="Employés" endpoint="/acct/employees" onMutate={loadEmployees}
            fields={[
              { key: "name", label: "Nom", type: "text", req: true },
              { key: "role", label: "Poste", type: "text" },
              { key: "base_salary", label: "Salaire de base", type: "number" },
            ]}
            columns={[{ key: "name", label: "Nom" }, { key: "role", label: "Poste" }, { key: "base_salary", label: "Salaire de base", money: true }]} />
        </TabsContent>

        <TabsContent value="loyer">
          <CrudSection title="Loyer" endpoint="/acct/rent" onMutate={reloadAll}
            fields={[
              { key: "label", label: "Libellé (ex: Loyer Août)", type: "text", req: true },
              { key: "planned_amount", label: "Montant", type: "number" },
              { key: "due_date", label: "Date prévue", type: "date", default: today() },
              { key: "paid_amount", label: "Montant payé", type: "number" },
              { key: "pay_date", label: "Date de paiement", type: "date", default: today() },
              { key: "method", label: "Mode", type: "select", options: METHODS, default: "Espèces" },
              { key: "note", label: "Note", type: "text" },
            ]}
            columns={[{ key: "pay_date", label: "Date", date: true }, { key: "label", label: "Libellé" }, { key: "paid_amount", label: "Payé", money: true }, { key: "remaining", label: "Reste", money: true }, { key: "status", label: "Statut", badge: true }]}
            hint="Si le mode est « Espèces », une sortie de caisse est créée automatiquement." />
        </TabsContent>

        <TabsContent value="dettes">
          <CrudSection title="Dettes / Avances / Apports / Retraits" endpoint="/acct/debts" onMutate={reloadAll}
            fields={[
              { key: "type", label: "Type", type: "select", options: DEBT_TYPES, default: "dette", req: true },
              { key: "creditor", label: "Créancier / Bénéficiaire", type: "text" },
              { key: "initial_amount", label: "Montant initial", type: "number" },
              { key: "paid_amount", label: "Montant payé", type: "number" },
              { key: "method", label: "Mode", type: "select", options: METHODS, default: "Espèces" },
              { key: "date", label: "Date", type: "date", default: today() },
              { key: "note", label: "Note", type: "text" },
            ]}
            columns={[{ key: "date", label: "Date", date: true }, { key: "type", label: "Type" }, { key: "creditor", label: "Créancier" }, { key: "initial_amount", label: "Initial", money: true }, { key: "paid_amount", label: "Payé", money: true }, { key: "remaining", label: "Reste", money: true }, { key: "status", label: "Statut", badge: true }]}
            hint="Remboursement / dette / retrait en espèces → sortie de caisse auto. Apports = entrées de trésorerie." />
        </TabsContent>

        <TabsContent value="recurrentes">
          <CrudSection title="Charges récurrentes (Loyer & Salaires)" endpoint="/acct/recurring" onMutate={reloadAll}
            hint="Générées automatiquement chaque mois en statut « À payer ». Vous les marquez payées dans les onglets Loyer / Salaires."
            renderExtra={(reload) => (
              <Button size="sm" variant="outline" data-testid="generate-recurring" onClick={async () => {
                try { const { data } = await api.post("/acct/recurring/generate"); toast.success(`${data.created} charge(s) générée(s)`); reload(); reloadAll(); }
                catch (e) { toast.error(apiError(e)); }
              }}><Repeat size={15} className="mr-1" /> Générer maintenant</Button>
            )}
            fields={[
              { key: "kind", label: "Type", type: "select", options: [{ value: "loyer", label: "Loyer" }, { value: "salaire", label: "Salaire" }], default: "loyer", req: true },
              { key: "label", label: "Libellé (pour le loyer)", type: "text" },
              { key: "employee_id", label: "Employé (si salaire)", type: "select", options: employees.map((e) => ({ value: e.id, label: e.name })) },
              { key: "amount", label: "Montant mensuel", type: "number", req: true },
              { key: "due_day", label: "Jour d'échéance (1-28)", type: "number", default: 1, req: true },
              { key: "method", label: "Mode", type: "select", options: METHODS, default: "Espèces" },
              { key: "active", label: "Active", type: "bool", default: true },
            ]}
            columns={[{ key: "kind", label: "Type", badge: true }, { key: "label", label: "Libellé" }, { key: "employee_name", label: "Employé" }, { key: "amount", label: "Montant", money: true }, { key: "due_day", label: "Échéance" }, { key: "active", label: "Active", bool: true }]} />
        </TabsContent>

        <TabsContent value="init">
          <InitSection onMutate={reloadAll} m={m} />
        </TabsContent>

        <TabsContent value="journal">
          <AuditSection />
        </TabsContent>
      </Tabs>

      {/* Modal détail (vue micro) */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl" data-testid="detail-dialog">
          <DialogHeader><DialogTitle>{detail?.title}</DialogTitle></DialogHeader>
          {detail && summary && <DetailTable rows={summary.details[detail.key] || []} kind={detail.key} m={m} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoTip({ text }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-indigo-600 ml-1 inline-flex align-middle" data-testid="info-tip"><Info size={13} /></button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-xs text-slate-600 leading-relaxed">{text}</PopoverContent>
    </Popover>
  );
}

function Kpi({ label, value, big, onClick, testid, tip }) {
  return (
    <div className={`flex items-center justify-between ${onClick ? "cursor-pointer hover:bg-slate-50 rounded-lg px-1 -mx-1" : ""}`} onClick={onClick} data-testid={testid}>
      <span className="text-sm text-slate-500">{label}{onClick && <span className="text-indigo-400 text-xs ml-1">(détail)</span>}{tip && <InfoTip text={tip} />}</span>
      <span className={`font-semibold text-slate-800 ${big ? "text-lg" : ""}`}>{value}</span>
    </div>
  );
}

const DETAIL_COLS = {
  closings: [{ k: "day", l: "Jour", d: true }, { k: "agent_name", l: "Agent" }, { k: "real_total", l: "Réel", money: true }, { k: "declared_cash", l: "Espèces", money: true }, { k: "non_cash_collected", l: "Autres", money: true }],
  sorties_caisse: [{ k: "day", l: "Jour", d: true }, { k: "category", l: "Catégorie" }, { k: "amount", l: "Montant", money: true }, { k: "note", l: "Note" }, { k: "agent_name", l: "Agent" }],
  hors_caisse: [{ k: "date", l: "Date", d: true }, { k: "category", l: "Catégorie" }, { k: "amount", l: "Montant", money: true }, { k: "method", l: "Moyen" }, { k: "note", l: "Note" }],
  salaires: [{ k: "pay_date", l: "Date", d: true }, { k: "employee_name", l: "Employé" }, { k: "label", l: "Période" }, { k: "disbursed", l: "Décaissé", money: true }, { k: "remaining", l: "Reste", money: true }, { k: "status", l: "Statut" }],
  loyer: [{ k: "pay_date", l: "Date", d: true }, { k: "label", l: "Libellé" }, { k: "paid_amount", l: "Payé", money: true }, { k: "remaining", l: "Reste", money: true }, { k: "status", l: "Statut" }],
  dettes: [{ k: "date", l: "Date", d: true }, { k: "type", l: "Type" }, { k: "creditor", l: "Créancier" }, { k: "paid_amount", l: "Payé", money: true }, { k: "remaining", l: "Reste", money: true }],
  creances: [{ k: "order_number", l: "Commande" }, { k: "customer_name", l: "Client" }, { k: "total", l: "Total", money: true }, { k: "paid", l: "Payé", money: true }, { k: "remaining", l: "Reste", money: true }],
};

function DetailTable({ rows, kind, m }) {
  const cols = DETAIL_COLS[kind] || [];
  if (!rows.length) return <p className="text-slate-400 text-center py-6">Aucune opération sur cette période.</p>;
  return (
    <div className="max-h-[60vh] overflow-auto">
      <Table>
        <TableHeader><TableRow>{cols.map((c) => <TableHead key={c.k}>{c.l}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.id || i}>
              {cols.map((c) => <TableCell key={c.k}>{c.money ? m(r[c.k]) : c.d ? fmtD(r[c.k]) : (r[c.k] ?? "—")}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CrudSection({ title, endpoint, fields, columns, onMutate, hint, renderExtra }) {
  const { settings } = useAuth();
  const cur = settings?.currency || "KMF";
  const m = (n) => fmtMoney(n || 0, cur);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 50;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint, { params: { page, limit: LIMIT } });
      const items = Array.isArray(data) ? data : (data.items || []);
      setList(items);
      setTotal(Array.isArray(data) ? items.length : (data.total || 0));
      if (!items.length && page > 1) setPage(page - 1);
    } catch (e) { toast.error(apiError(e)); }
  }, [endpoint, page]);
  useEffect(() => { load(); }, [load]);

  const open = (item) => {
    const base = {};
    fields.forEach((f) => { base[f.key] = item ? (item[f.key] ?? "") : (f.default ?? ""); });
    setForm({ _id: item?.id || null, ...base });
  };
  const save = async () => {
    const payload = {};
    for (const f of fields) { let v = form[f.key]; if (f.type === "number") v = parseFloat(v) || 0; else if (f.type === "bool") v = v === true || v === "true"; payload[f.key] = v; }
    try {
      if (form._id) await api.put(`${endpoint}/${form._id}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Enregistré"); setForm(null); load(); onMutate?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (item) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try { await api.delete(`${endpoint}/${item.id}`); toast.success("Supprimé"); load(); onMutate?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const opts = (f) => (f.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const canSave = form && fields.every((f) => {
    if (!f.req) return true;
    const v = form[f.key];
    if (f.type === "number") return parseFloat(v) > 0;
    return v !== "" && v != null;
  });

  return (
    <Card className="p-4 mt-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <div className="flex gap-2">
          {renderExtra && renderExtra(load)}
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => open(null)} data-testid={`add-${endpoint.split("/").pop()}`}><Plus size={15} className="mr-1" /> Ajouter</Button>
        </div>
      </div>
      {hint && <p className="text-xs text-amber-600 mb-2">ℹ️ {hint}</p>}
      <div className="overflow-auto">
        <Table>
          <TableHeader><TableRow>{columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}<TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((it) => (
              <TableRow key={it.id} data-testid={`row-${it.id}`}>
                {columns.map((c) => (
                  <TableCell key={c.key}>
                    {c.money ? m(it[c.key]) : c.date ? fmtD(it[c.key]) : c.bool ? (it[c.key] ? "Oui" : "Non") : c.badge ? <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{it[c.key]}</span> : (it[c.key] ?? "—")}
                  </TableCell>
                ))}
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => open(it)} data-testid={`edit-${it.id}`}><Pencil size={14} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(it)} data-testid={`del-${it.id}`}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-slate-400 py-6">Aucun élément</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-between mt-3 text-sm">
          <span className="text-slate-500" data-testid={`pageinfo-${endpoint.split("/").pop()}`}>Page {page} / {pages} — {total} au total</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid={`prev-${endpoint.split("/").pop()}`}>Précédent</Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid={`next-${endpoint.split("/").pop()}`}>Suivant</Button>
          </div>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent data-testid="crud-dialog">
          <DialogHeader><DialogTitle>{form?._id ? "Modifier" : "Ajouter"} — {title}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3 max-h-[60vh] overflow-auto">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="text-sm text-slate-600">{f.label}</label>
                  {f.type === "select" ? (
                    <select className="w-full border rounded-lg px-3 py-2 text-sm bg-white" value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`}>
                      <option value="">— Choisir —</option>
                      {opts(f).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === "bool" ? (
                    <select className="w-full border rounded-lg px-3 py-2 text-sm bg-white" value={form[f.key] === true || form[f.key] === "true" ? "true" : "false"} onChange={(e) => setForm({ ...form, [f.key]: e.target.value === "true" })} data-testid={`field-${f.key}`}>
                      <option value="true">Oui</option>
                      <option value="false">Non</option>
                    </select>
                  ) : (
                    <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Annuler</Button><Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={!canSave} data-testid="crud-save">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function InitSection({ onMutate, m }) {
  const [form, setForm] = useState({ init_date: today(), starting_treasury: "", note: "" });
  const [saved, setSaved] = useState(null);
  useEffect(() => {
    api.get("/acct/init").then(({ data }) => { if (data) { setSaved(data); setForm({ init_date: data.init_date, starting_treasury: data.starting_treasury, note: data.note || "" }); } }).catch(() => {});
  }, []);
  const save = async () => {
    try {
      const { data } = await api.post("/acct/init", { init_date: form.init_date, starting_treasury: parseFloat(form.starting_treasury) || 0, note: form.note });
      setSaved(data); toast.success("Initialisation enregistrée"); onMutate?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-4 mt-3 space-y-3 max-w-lg" data-testid="init-section">
      <h3 className="font-semibold text-slate-800">Initialisation de la comptabilité</h3>
      <p className="text-xs text-slate-500">Point de départ pour le calcul de la trésorerie. Les données historiques de la caisse sont récupérées automatiquement, sans être modifiées.</p>
      <div><label className="text-sm text-slate-600">Date d'initialisation</label><Input type="date" value={form.init_date} onChange={(e) => setForm({ ...form, init_date: e.target.value })} data-testid="init-date" /></div>
      <div><label className="text-sm text-slate-600">Trésorerie réelle de départ</label><Input type="number" value={form.starting_treasury} onChange={(e) => setForm({ ...form, starting_treasury: e.target.value })} data-testid="init-treasury" /></div>
      <div><label className="text-sm text-slate-600">Note</label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
      {saved && <p className="text-xs text-emerald-600">Enregistré le {fmtD(saved.init_date)} · Départ {m(saved.starting_treasury)}</p>}
      <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} data-testid="init-save">Enregistrer l'initialisation</Button>
    </Card>
  );
}

function AuditSection() {
  const [log, setLog] = useState([]);
  useEffect(() => { api.get("/acct/audit").then(({ data }) => setLog(data)).catch(() => {}); }, []);
  return (
    <Card className="p-4 mt-3" data-testid="audit-section">
      <div className="flex items-center gap-2 mb-3 font-semibold text-slate-800"><FileClock size={18} /> Journal de traçabilité</div>
      <div className="overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader><TableRow><TableHead>Date/heure</TableHead><TableHead>Utilisateur</TableHead><TableHead>Entité</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {log.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="whitespace-nowrap text-xs">{new Date(l.at).toLocaleString("fr-FR")}</TableCell>
                <TableCell>{l.user_name || "—"}</TableCell>
                <TableCell>{l.entity}</TableCell>
                <TableCell><span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{l.action}</span></TableCell>
              </TableRow>
            ))}
            {!log.length && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">Aucune opération enregistrée</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
